// Client-side logging utility
// In a production application, you would integrate with a proper service
// like LogRocket, Sentry, or New Relic

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEvent {
  level: LogLevel;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
}

class Logger {
  private isProduction: boolean;
  private logBuffer: LogEvent[] = [];
  private maxBufferSize = 100;
  
  constructor() {
    this.isProduction = import.meta.env.PROD === true;
  }
  
  // Log with debug level
  debug(message: string, data?: Record<string, unknown>) {
    this.log('debug', message, data);
  }
  
  // Log with info level
  info(message: string, data?: Record<string, unknown>) {
    this.log('info', message, data);
  }
  
  // Log with warning level
  warn(message: string, data?: Record<string, unknown>) {
    this.log('warn', message, data);
  }
  
  // Log with error level
  error(message: string, data?: Record<string, unknown>) {
    this.log('error', message, data);
  }
  
  // Main log method
  private log(level: LogLevel, message: string, data?: Record<string, unknown>) {
    // Create the log event
    const event: LogEvent = {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
    };
    
    // Store in buffer (for potential sending to server)
    this.addToBuffer(event);
    
    // Console logging for development
    if (!this.isProduction || level === 'error') {
      const formattedMessage = `[${level.toUpperCase()}] ${message}`;
      
      // Fix for TypeScript error by using specific console methods
      if (data) {
        switch (level) {
          case 'debug':
            console.log(formattedMessage, data);
            break;
          case 'info':
            console.info(formattedMessage, data);
            break;
          case 'warn':
            console.warn(formattedMessage, data);
            break;
          case 'error':
            console.error(formattedMessage, data);
            break;
        }
      } else {
        switch (level) {
          case 'debug':
            console.log(formattedMessage);
            break;
          case 'info':
            console.info(formattedMessage);
            break;
          case 'warn':
            console.warn(formattedMessage);
            break;
          case 'error':
            console.error(formattedMessage);
            break;
        }
      }
    }
  }
  
  // Add to log buffer with size limit
  private addToBuffer(event: LogEvent) {
    this.logBuffer.push(event);
    
    // If buffer exceeds max size, remove oldest events
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer = this.logBuffer.slice(-this.maxBufferSize);
    }
    
    // In a real application, you might send logs to the server
    // on certain conditions (buffer size, specific error levels, etc.)
    if (event.level === 'error' && this.isProduction) {
      this.sendLogsToServer();
    }
  }
  
  // Send logs to server (placeholder implementation)
  async sendLogsToServer() {
    if (this.logBuffer.length === 0) return;
    
    try {
      // In a real application, you would send logs to your backend
      // or directly to a logging service
      console.log('Sending logs to server:', this.logBuffer.length, 'events');
      
      // After successful sending, clear the buffer
      this.logBuffer = [];
    } catch (error) {
      console.error('Failed to send logs to server:', error);
    }
  }
}

// Create a singleton instance
export const logger = new Logger(); 